// Ported from microsites/womens-conference/src/components/KeynoteCard.tsx.
// Changes: next/image -> img via art(); the config arrives as a prop; her
// sessions are read off MP's agenda (lib/views.ts buildAgenda).
import { useState } from 'react';
import { speakerByKey } from '../lib/conference';
import type { ConferenceConfig, Speaker } from '../lib/conference';
import type { AgendaItem } from '../lib/conference';
import { clockLabel } from '../lib/format';
import { art } from '../lib/art';
import { Vine } from './Botanical';
import { Sheet, SheetBand } from './Sheet';

/** Inside her own sheet, "Session One with Elizabeth Woodson" is just
 *  "Session One". */
const sessionTitle = (title: string) => title.replace(/ with .+$/, '');

/**
 * The keynote gets a hero card, not a grid cell: one speaker carrying three
 * main sessions is the event's spine. The card stays short; the sheet holds
 * the longer story and hands off to her own site.
 */
export function KeynoteCard({
  config,
  overlap = false,
  agenda,
  keynote,
}: {
  config: ConferenceConfig;
  overlap?: boolean;
  /**
   * Her, from `gathering_people`. Omitted falls back to the config, same rule
   * the agenda below follows: a database having a morning should never blank
   * the card the whole weekend is built around.
   */
  keynote?: Speaker;
  /**
   * The run of show, from our database. Omitted or empty falls back to the
   * config, which is the same rule every other reader here follows: a database
   * having a morning should never blank a card.
   */
  agenda?: AgendaItem[];
}) {
  const [open, setOpen] = useState(false);
  const speaker = keynote ?? speakerByKey(config, config.keynoteKey);
  if (!speaker) return null;
  const dayLabel = (key: string) => config.days.find((d) => d.key === key)?.label ?? '';
  // Hardcoded twice before this, once on the card and once in the sheet. A
  // missing hero is not a broken card: the sheet simply opens on her name.
  const hero = speaker.cardImage ? art(speaker.cardImage) : undefined;
  const items = agenda?.length ? agenda : config.agenda;
  const sessions = items.filter((i) => i.speakerKey === speaker.key && i.start);

  return (
    <section
      // z-[61] clears the paper coat (body::before, z-60), so this one card
      // stays clean petal while the rest of the page keeps its fibers.
      // Sheets sit at 70/75, above both.
      className={
        overlap ? 'wm-shell relative z-[61] -mt-10 sm:-mt-12' : 'wm-shell relative z-[61] mt-16'
      }
    >
      {/* The whole card is the button; the pill inside is just the affordance. */}
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="wm-card wm-lift group relative grid w-full gap-6 overflow-hidden p-6 text-left sm:p-8 md:grid-cols-[auto_1fr] md:items-center"
      >
        {/* Plain petal behind her, on purpose: her portrait is the only
            picture this card needs. The drawn stem stays, top corner only —
            the read-more sits bottom right and a stem through it would look
            like a smudge. */}
        <Vine
          aria-hidden
          className="text-wm-sage-300/55 pointer-events-none absolute -top-20 -right-3 hidden h-72 w-14 rotate-[8deg] sm:block"
        />
        {hero ? (
          <img
            src={hero}
            alt={speaker.name}
            width={1657}
            height={1015}
            className="mx-auto aspect-[4/3] w-full max-w-sm rounded-2xl object-cover md:w-96"
          />
        ) : null}
        <div>
          <h2 className="wm-display text-4xl sm:text-5xl">{speaker.name}</h2>
          <p className="text-wm-sage-500 mt-1 text-sm font-medium">{speaker.tagline}</p>
          <p className="mt-4 max-w-2xl">{speaker.bio}</p>
          {/* The card is the button, so this is a hint, not a target: no
              container, tucked into the corner the eye lands on last. */}
          <span className="text-wm-sage-500 group-hover:text-wm-sage-800 mt-5 flex items-center justify-end gap-1.5 text-sm font-medium transition-colors">
            More about Elizabeth
            <svg
              aria-hidden
              viewBox="0 0 16 16"
              className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.75"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M3 8h10M9 4l4 4-4 4" />
            </svg>
          </span>
        </div>
      </button>

      <Sheet
        open={open}
        onClose={() => setOpen(false)}
        title={`About ${speaker.name}`}
        // Her portrait is the header, whole and edge to edge, with her name
        // set along its foot. No portrait: the painted band carries the name.
        band={
          <SheetBand
            kicker="Keynote speaker"
            title={speaker.name}
            subtitle={speaker.tagline}
            image={hero}
            imageAlt={speaker.name}
            imageSize={{ width: 1657, height: 1015 }}
          />
        }
      >
        <div className="flex flex-col gap-6 p-6 sm:px-7">
          <p className="text-wm-sage-700 text-sm">{speaker.bio}</p>

          {/* Her weekend, read straight off the agenda: the one thing about
              her that is specific to this conference. */}
          {sessions.length ? (
            <div>
              <p className="wm-kicker mb-2">At the conference</p>
              <ul className="flex flex-col gap-2">
                {sessions.map((item) => (
                  <li
                    key={`${item.day}-${item.sequence}`}
                    className="bg-wm-cream/70 flex items-baseline justify-between gap-3 rounded-xl px-4 py-3"
                  >
                    <span className="text-sm font-medium">{sessionTitle(item.title)}</span>
                    <span className="text-wm-sage-500 shrink-0 text-xs font-semibold">
                      {dayLabel(item.day)} {clockLabel(item.start!)}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          ) : null}

          {speaker.background ? (
            <div>
              <p className="wm-kicker mb-2">Background</p>
              <p className="text-wm-sage-700 text-sm">{speaker.background}</p>
            </div>
          ) : null}

          {speaker.books?.length ? (
            <div>
              <p className="wm-kicker mb-3">Books</p>
              {/* The covers say the titles themselves, and every one of them
                  is a door: no captions, no tooltip, just the art. */}
              <ul className="grid grid-cols-2 gap-3">
                {speaker.books.map((book) => (
                  <li key={book.title}>
                    <a
                      href={book.url}
                      target="_blank"
                      rel="noreferrer"
                      aria-label={`${book.title} by ${speaker.name}`}
                      className="flex aspect-4/5 items-center justify-center transition-transform duration-200 hover:scale-[1.03] active:scale-[0.99]"
                    >
                      {book.cover ? (
                        <img
                          src={art(book.cover)}
                          alt={`Cover of ${book.title}`}
                          width={640}
                          height={800}
                          className={`h-full w-auto object-contain ${
                            book.bleed ? 'shadow-wm-sage-950/25 shadow-lg' : ''
                          }`}
                        />
                      ) : null}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
          {/* Same quiet treatment as the card's own "More about" hint, and in
              the same corner: this is the last thing in the sheet, and a
              marigold pill made leaving the page look like the main event.
              Named group so the arrow answers to this link and not to the
              card wrapper it sits inside. */}
          {speaker.url ? (
            <a
              href={speaker.url}
              target="_blank"
              rel="noreferrer"
              className="group/site text-wm-sage-500 hover:text-wm-sage-800 flex items-center gap-1.5 self-end text-sm font-medium transition-colors"
            >
              Visit elizabethwoodson.net
              <svg
                aria-hidden
                viewBox="0 0 16 16"
                className="h-3.5 w-3.5 transition-transform group-hover/site:translate-x-0.5"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.75"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M3 8h10M9 4l4 4-4 4" />
              </svg>
            </a>
          ) : null}
        </div>
      </Sheet>
    </section>
  );
}
